import React, { useMemo } from 'react';
import { useInfiniteHits } from 'react-instantsearch';

/**
 * Wrapper for the legacy connectInfiniteHits HOC from react-instantsearch-dom v6.
 * This provides the same API but uses the v7 react-instantsearch hook internally.
 *
 * CRITICAL: The hookParams must be memoized to prevent infinite re-render loops.
 * Creating a new object on every render causes useInfiniteHits to see changed
 * dependencies, triggering cascading re-renders.
 *
 * @param {Function} Component - A component that receives infinite hits data as props
 * @returns {React.Component} - A wrapped component connected to InstantSearch infinite hits
 */
export const connectInfiniteHits = Component => {
  const ConnectedComponent = props => {
    // Only include the specific props that are valid for useInfiniteHits
    const { escapeHTML, transformItems, cache, showPrevious } = props;

    // Memoize hook parameters to prevent infinite re-renders
    const hookParams = useMemo(
      () => ({
        escapeHTML,
        transformItems,
        cache,
        showPrevious,
      }),
      [escapeHTML, transformItems, cache, showPrevious]
    );

    const data = useInfiniteHits(hookParams);

    return <Component {...props} {...data} />;
  };

  ConnectedComponent.displayName = `connectInfiniteHits(${Component.displayName ||
    Component.name ||
    'Component'})`;

  return ConnectedComponent;
};

export default connectInfiniteHits;
